import { formatImportErrorContext } from "components/ImportData/utils";
import type { ICorrectionColumnData } from "components/shared/CorrectionTable";
import CorrectionTable from "components/shared/CorrectionTable";
import type { TFileData } from "containers/BulkUpdatesContainer";
import { SmallBodyHighlight } from "helpers/TypographyComponents";
import { groupObjByKey } from "helpers/utils";
import type { Dispatch, SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import type { TImportError } from "store/dataImportSlice";

import styles from "../style.module.scss";

const columnTitleByMappedKey = (selectedValues: Record<string, string>) => {
    const byKey = new Map<string, string>();
    for (const [title, key] of Object.entries(selectedValues)) {
        if (!key || key === "Ignore" || key === "NotMandatory") continue;
        if (!byKey.has(key)) byKey.set(key, title);
        const stripped = key.replace("_", "");
        if (stripped !== key && !byKey.has(stripped)) byKey.set(stripped, title);
    }
    return byKey;
};

const storedColumnKey = (mappedKey: string) => (mappedKey === "advancePayment" ? "initialBalance" : mappedKey);

const BulkUpdateErrorLine = ({ error, columnTitle }: { error: TImportError; columnTitle?: string }) => {
    const [t] = useTranslation();
    const context = formatImportErrorContext(
        {
            mappedKey: columnTitle ? error.mappedKey : undefined,
            value: error.value,
            channelIndex: error.channelIndex,
        },
        columnTitle && error.mappedKey ? { [error.mappedKey]: columnTitle } : {},
    );

    return (
        <div className={styles["error-data"]}>
            {context ? <span>{context} -</span> : null}
            <span>{error.message ? t(error.message) : ""}</span>
        </div>
    );
};

interface IUpdateErrors {
    errors: TImportError[];
    fileTitles: string[];
    selectedValues: Record<string, string>;
    loadedData: TFileData | undefined;
    floatKeys: string[];
    setLoadedData: Dispatch<SetStateAction<TFileData | undefined>>;
}

/**
 * Component that display errors from import: either as table when errors <= 100 or in lines.
 */
const UpdateErrors = ({ errors, fileTitles, selectedValues, loadedData, floatKeys, setLoadedData }: IUpdateErrors) => {
    const [t] = useTranslation();
    const safeErrors = errors ?? [];

    /**
     * Matches their titles with our keys (based on selectedValues)
     * and saves the original index (column in their file).
     */
    const formattedColumns = () => {
        const titles: ICorrectionColumnData[] = [];

        fileTitles.forEach((title, index) => {
            const key = selectedValues[title];
            if (key !== "Ignore") titles.push({ key: key.replace("_", ""), title, originalIndex: index });
        });

        return titles;
    };

    /**
     * Errors grouped by line for the display line by line (when safeErrors.length > 100).
     */
    const groupedErrors = groupObjByKey("fileLine", safeErrors);
    const columnTitles = columnTitleByMappedKey(selectedValues);

    return (
        <>
            {safeErrors.length <= 100 ? (
                <CorrectionTable
                    errors={safeErrors}
                    loadedData={loadedData ?? []}
                    setLoadedData={setLoadedData}
                    columns={formattedColumns()}
                    floatKeys={floatKeys}
                />
            ) : (
                <div className={styles["error-lines-container"]}>
                    {Object.keys(groupedErrors).map((line) => {
                        return (
                            <div key={`${line}_errorLines`} className={styles["error-lines"]}>
                                <SmallBodyHighlight style={{ textDecoration: "underline" }}>
                                    {t("ImportData.FileLine") + line}
                                </SmallBodyHighlight>
                                {groupedErrors[line].map((error: TImportError, index: number) => (
                                    <BulkUpdateErrorLine
                                        key={`${line}-${error.mappedKey}-${index}`}
                                        error={error}
                                        columnTitle={columnTitles.get(storedColumnKey(error.mappedKey))}
                                    />
                                ))}
                            </div>
                        );
                    })}
                </div>
            )}
        </>
    );
};

export default UpdateErrors;
